import Card from './Card';

import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from '../utils/debugUtils';
class Deck {
    constructor() {
        this.cards = [];
        this.initialize();
    }

    initialize() {
        const suits = ['oros', 'copas', 'espadas', 'bastos'];
        // Spanish deck usually has 40 cards: 1-7 and 10-12 (Sota, Caballo, Rey)
        // Some variations have 48 (1-9, 10-12) or 50.
        // For Ronda/Baciga, standard 40 cards is typical.
        const values = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12];

        this.cards = [];
        for (const suit of suits) {
            for (const value of values) {
                this.cards.push(new Card(suit, value));
            }
        }
    }

    shuffle() {
        // Fisher-Yates shuffle
        for (let i = this.cards.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [this.cards[i], this.cards[j]] = [this.cards[j], this.cards[i]];
        }
    }

    deal(count) {
        if (count > this.cards.length) {
            debugWarn('Not enough cards in deck to deal ' + count);
            return this.cards.splice(0, this.cards.length);
        }
        return this.cards.splice(0, count);
    }

    /**
     * Deal a single card from the top of the deck
     * @returns {Card|null} The dealt card or null if deck is empty
     */
    dealOne() {
        if (this.cards.length === 0) {
            debugWarn('No cards left in deck');
            return null;
        }
        return this.cards.shift();
    }

    /**
     * Insert a card at a random position in the deck
     * @param {Card} card - The card to insert
     */
    insertAtRandomPosition(card) {
        if (this.cards.length === 0) {
            this.cards.push(card);
            return;
        }
        const randomIndex = Math.floor(Math.random() * this.cards.length);
        this.cards.splice(randomIndex, 0, card);
    }

    prepareDebugDeck() {
        debugLog('🐞 Preparing DEBUG deck for testing scenarios...');

        // Define specific cards for specific positions (0-indexed)
        // Scenario: Dealer is Player 3 (Left), so deal starts with Player 0 (Bottom/Human)
        // Sequence of deal in first hand:
        // Round 1: Center(0), P0(1), P1(2), P2(3), P3(4)
        // Round 2: Center(5), P0(6), P1(7), P2(8), P3(9)
        // Round 3: Center(10), P0(11), P1(12), P2(13), P3(14)
        // Round 4: Center(15)

        // Goal: Test envío rechazado and media display
        // P0 (Human): 1, 1, 5 (Ronda of 1s) - Team 1
        // P1 (Right): 3, 3, 6 (Ronda of 3s) - Team 2
        // P2 (Top): 10, 11, 12 (no ronda) - Team 1
        // P3 (Left): 2, 4, 7 (no ronda) - Team 2
        // This sets up an envío between team 1 (P0) and team 2 (P1)

        const fixedCards = {
            0: { suit: 'oros', value: 5 },     // Center
            1: { suit: 'oros', value: 1 },     // P0: 1 Oros
            2: { suit: 'copas', value: 3 },    // P1: 3 Copas
            3: { suit: 'espadas', value: 10 }, // P2: 10 Espadas
            4: { suit: 'bastos', value: 2 },   // P3: 2 Bastos

            5: { suit: 'copas', value: 7 },    // Center
            6: { suit: 'espadas', value: 1 },  // P0: 1 Espadas -> Ronda (1,1)
            7: { suit: 'espadas', value: 3 },  // P1: 3 Espadas -> Ronda (3,3)
            8: { suit: 'copas', value: 11 },   // P2: 11 Copas
            9: { suit: 'espadas', value: 4 },  // P3: 4 Espadas

            10: { suit: 'bastos', value: 4 },  // Center
            11: { suit: 'copas', value: 5 },   // P0: 5 Copas
            12: { suit: 'bastos', value: 6 },  // P1: 6 Bastos
            13: { suit: 'bastos', value: 12 }, // P2: 12 Bastos
            14: { suit: 'espadas', value: 7 }, // P3: 7 Espadas

            15: { suit: 'bastos', value: 1 }   // Center
        };

        // Create all 40 cards
        const allCards = [];
        const suits = ['oros', 'copas', 'espadas', 'bastos'];
        const values = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12];

        for (const suit of suits) {
            for (const value of values) {
                allCards.push(new Card(suit, value));
            }
        }

        // Filter out the fixed cards from the available pool
        let availableCards = allCards.filter(c => {
            return !Object.values(fixedCards).some(fc => fc.suit === c.suit && fc.value === c.value);
        });

        // Shuffle available cards to fill the gaps randomly
        // Fisher-Yates for available
        for (let i = availableCards.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [availableCards[i], availableCards[j]] = [availableCards[j], availableCards[i]];
        }

        // Construct the final deck
        this.cards = [];
        for (let i = 0; i < 40; i++) {
            if (fixedCards[i]) {
                const fc = fixedCards[i];
                this.cards.push(new Card(fc.suit, fc.value));
            } else {
                this.cards.push(availableCards.pop());
            }
        }
    }

    /**
     * Debug deck where 3 players get Ronda on the first deal
     * P0 (Human): Ronda of 1s (1 oros, 1 espadas, 5 copas)
     * P1 (Right): Ronda of 3s (3 copas, 3 espadas, 6 bastos)
     * P2 (Top): Ronda of 10s (10 espadas, 10 copas, 12 bastos)
     * P3 (Left): No ronda (2 bastos, 4 espadas, 7 espadas)
     *
     * Teams: P0+P2 (Team 1), P1+P3 (Team 2)
     * Result: Both teams have rondas, triggering envío
     */
    prepareDebugDeckTripleRonda() {
        debugLog('🐞 Preparing DEBUG deck for TRIPLE RONDA scenario...');

        // Dealer is Player 3 (Left), so deal starts with Player 0 (Bottom/Human)
        // Sequence of deal in first hand:
        // Round 1: Center(0), P0(1), P1(2), P2(3), P3(4)
        // Round 2: Center(5), P0(6), P1(7), P2(8), P3(9)
        // Round 3: Center(10), P0(11), P1(12), P2(13), P3(14)
        // Round 4: Center(15)

        // Goal: 3 players get Ronda
        // P0 (Human): 1, 1, 5 (Ronda of 1s) - Team 1
        // P1 (Right): 3, 3, 6 (Ronda of 3s) - Team 2
        // P2 (Top): 10, 10, 12 (Ronda of 10s) - Team 1
        // P3 (Left): 2, 4, 7 (no ronda) - Team 2

        const fixedCards = {
            0: { suit: 'oros', value: 5 },     // Center
            1: { suit: 'oros', value: 1 },     // P0: 1 Oros
            2: { suit: 'copas', value: 3 },    // P1: 3 Copas
            3: { suit: 'espadas', value: 10 }, // P2: 10 Espadas
            4: { suit: 'bastos', value: 2 },   // P3: 2 Bastos

            5: { suit: 'copas', value: 7 },    // Center
            6: { suit: 'espadas', value: 1 },  // P0: 1 Espadas -> Ronda (1,1)
            7: { suit: 'espadas', value: 3 },  // P1: 3 Espadas -> Ronda (3,3)
            8: { suit: 'copas', value: 10 },   // P2: 10 Copas -> Ronda (10,10)
            9: { suit: 'espadas', value: 4 },  // P3: 4 Espadas

            10: { suit: 'bastos', value: 4 },  // Center
            11: { suit: 'copas', value: 5 },   // P0: 5 Copas
            12: { suit: 'bastos', value: 6 },  // P1: 6 Bastos
            13: { suit: 'bastos', value: 12 }, // P2: 12 Bastos
            14: { suit: 'espadas', value: 7 }, // P3: 7 Espadas

            15: { suit: 'bastos', value: 1 }   // Center
        };

        // Create all 40 cards
        const allCards = [];
        const suits = ['oros', 'copas', 'espadas', 'bastos'];
        const values = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12];

        for (const suit of suits) {
            for (const value of values) {
                allCards.push(new Card(suit, value));
            }
        }

        // Filter out the fixed cards from the available pool
        let availableCards = allCards.filter(c => {
            return !Object.values(fixedCards).some(fc => fc.suit === c.suit && fc.value === c.value);
        });

        // Shuffle available cards to fill the gaps randomly
        for (let i = availableCards.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [availableCards[i], availableCards[j]] = [availableCards[j], availableCards[i]];
        }

        // Construct the final deck
        this.cards = [];
        for (let i = 0; i < 40; i++) {
            if (fixedCards[i]) {
                const fc = fixedCards[i];
                this.cards.push(new Card(fc.suit, fc.value));
            } else {
                this.cards.push(availableCards.pop());
            }
        }

        debugLog('🐞 DEBUG Triple Ronda Deck Ready!');
        debugLog('  P0: 1♦, 1♠, 5♥ = Ronda of 1s (Team 1)');
        debugLog('  P1: 3♥, 3♠, 6♣ = Ronda of 3s (Team 2)');
        debugLog('  P2: 10♠, 10♥, 12♣ = Ronda of 10s (Team 1)');
        debugLog('  P3: 2♣, 4♠, 7♠ = No ronda (Team 2)');
    }

    /**
     * Debug deck where all 4 players get a 5 in their first card.
     * Designed to test Caída → Contracaída → San Vicente chain.
     * To trigger: P0 plays 5♦ first, then P1/P2/P3 play their 5s in sequence.
     *
     * P0 (Human): 5♦, 11♠, 1♥  - Team 1
     * P1 (Right): 5♥, 4♣, 12♠  - Team 2
     * P2 (Top):   5♠, 6♦, 4♦   - Team 1
     * P3 (Left):  5♣, 10♠, 11♥ - Team 2
     * Center:     2♦, 3♥, 7♣, 12♣
     */
    prepareDebugDeckSanVicente() {
        debugLog('🐞 Preparing DEBUG deck for SAN VICENTE / CONTRACAÍDA scenario...');

        // Dealer is Player 3 (Left), so deal starts with Player 0 (Bottom/Human)
        // Sequence of deal in first hand:
        // Round 1: Center(0), P0(1), P1(2), P2(3), P3(4)
        // Round 2: Center(5), P0(6), P1(7), P2(8), P3(9)
        // Round 3: Center(10), P0(11), P1(12), P2(13), P3(14)
        // Round 4: Center(15)

        const fixedCards = {
            0:  { suit: 'oros',    value: 2  }, // Center: 2♦
            1:  { suit: 'oros',    value: 5  }, // P0: 5♦  ← inicia la cadena
            2:  { suit: 'copas',   value: 5  }, // P1: 5♥  ← Caída
            3:  { suit: 'espadas', value: 5  }, // P2: 5♠  ← Contracaída
            4:  { suit: 'bastos',  value: 5  }, // P3: 5♣  ← San Vicente

            5:  { suit: 'copas',   value: 3  }, // Center: 3♥
            6:  { suit: 'espadas', value: 11 }, // P0: 11♠
            7:  { suit: 'bastos',  value: 4  }, // P1: 4♣
            8:  { suit: 'oros',    value: 6  }, // P2: 6♦
            9:  { suit: 'espadas', value: 10 }, // P3: 10♠

            10: { suit: 'bastos',  value: 7  }, // Center: 7♣
            11: { suit: 'copas',   value: 1  }, // P0: 1♥
            12: { suit: 'espadas', value: 12 }, // P1: 12♠
            13: { suit: 'oros',    value: 4  }, // P2: 4♦
            14: { suit: 'copas',   value: 11 }, // P3: 11♥

            15: { suit: 'bastos',  value: 12 }, // Center: 12♣
        };

        const allCards = [];
        const suits = ['oros', 'copas', 'espadas', 'bastos'];
        const values = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12];

        for (const suit of suits) {
            for (const value of values) {
                allCards.push(new Card(suit, value));
            }
        }

        let availableCards = allCards.filter(c => {
            return !Object.values(fixedCards).some(fc => fc.suit === c.suit && fc.value === c.value);
        });

        for (let i = availableCards.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [availableCards[i], availableCards[j]] = [availableCards[j], availableCards[i]];
        }

        this.cards = [];
        for (let i = 0; i < 40; i++) {
            if (fixedCards[i]) {
                const fc = fixedCards[i];
                this.cards.push(new Card(fc.suit, fc.value));
            } else {
                this.cards.push(availableCards.pop());
            }
        }

        debugLog('🐞 DEBUG San Vicente Deck Ready! Juega el 5 primero para iniciar la cadena.');
        debugLog('  P0: 5♦, 11♠, 1♥  (Team 1)');
        debugLog('  P1: 5♥, 4♣, 12♠  (Team 2)');
        debugLog('  P2: 5♠, 6♦, 4♦   (Team 1)');
        debugLog('  P3: 5♣, 10♠, 11♥ (Team 2)');
        debugLog('  Centro: 2♦, 3♥, 7♣, 12♣');
    }

    prepareDebugDeckBaciga() {
        debugLog('🐞 Preparing DEBUG deck for BACIGA with wildcard...');

        // Baciga dealing sequence (3 cards per player):
        // The dealer starts from the GameStartSequence cutting phase
        // From logs, dealer was P1 (index 1), dealing order: P2, P3, P0, P1
        // Card positions: P2(0,4,8), P3(1,5,9), P0(2,6,10), P1(3,7,11)
        //
        // IMPORTANT: We want P0 (Human player) to receive the wildcard
        // P0 receives positions 2, 6, 10

        // Goal: Give P0 a wildcard (7 oros) with a potential Bacigote
        // P0 (Human): 7 oros (wildcard), 5 copas, 5 espadas = Bacigote of 5s

        const fixedCards = {
            0: { suit: 'espadas', value: 11 }, // P2: 11 Espadas
            1: { suit: 'bastos', value: 10 },  // P3: 10 Bastos
            2: { suit: 'oros', value: 7 },     // P0: 7 Oros (WILDCARD!)
            3: { suit: 'copas', value: 12 },   // P1: 12 Copas

            4: { suit: 'bastos', value: 6 },   // P2: 6 Bastos
            5: { suit: 'oros', value: 3 },     // P3: 3 Oros
            6: { suit: 'copas', value: 5 },    // P0: 5 Copas
            7: { suit: 'espadas', value: 2 },  // P1: 2 Espadas

            8: { suit: 'oros', value: 1 },     // P2: 1 Oros
            9: { suit: 'bastos', value: 4 },   // P3: 4 Bastos
            10: { suit: 'espadas', value: 5 }, // P0: 5 Espadas -> P0 has 7♦, 5♥, 5♠ = BACIGOTE!
            11: { suit: 'copas', value: 6 }    // P1: 6 Copas
        };

        // Create all 40 cards
        const allCards = [];
        const suits = ['oros', 'copas', 'espadas', 'bastos'];
        const values = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12];

        for (const suit of suits) {
            for (const value of values) {
                allCards.push(new Card(suit, value));
            }
        }

        // Filter out the fixed cards from the available pool
        let availableCards = allCards.filter(c => {
            return !Object.values(fixedCards).some(fc => fc.suit === c.suit && fc.value === c.value);
        });

        // Shuffle available cards to fill the gaps randomly
        for (let i = availableCards.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [availableCards[i], availableCards[j]] = [availableCards[j], availableCards[i]];
        }

        // Construct the final deck
        this.cards = [];
        for (let i = 0; i < 40; i++) {
            if (fixedCards[i]) {
                const fc = fixedCards[i];
                this.cards.push(new Card(fc.suit, fc.value));
            } else {
                this.cards.push(availableCards.pop());
            }
        }

        debugLog('🐞 DEBUG Baciga Deck Ready! P0 will receive: 7♦ (wildcard), 5♥, 5♠ = BACIGOTE of 5s');
    }

    /**
     * Add cards to the bottom of the deck
     * @param {Card[]} cards - Array of cards to add to the bottom
     */
    addToBottom(cards) {
        if (!cards || cards.length === 0) return;

        // Clear assignedValue from any wildcards being added back to the deck
        // This ensures cards are in a clean state when recycled
        cards.forEach(card => {
            if (card.assignedValue !== undefined) {
                debugLog(`🃏 [Deck] Clearing assignedValue ${card.assignedValue} from wildcard being added to deck:`, card.value, card.suit);
                delete card.assignedValue;
            }
        });

        this.cards.push(...cards);
        debugLog(`🃏 [Deck] Added ${cards.length} cards to bottom. Deck now has ${this.cards.length} cards`);
    }

    reset() {
        this.initialize();
        this.shuffle();
    }

    get remaining() {
        return this.cards.length;
    }
}

export default Deck;
